/**
 * Preferencias de conversación per-usuario — contrato canónico client ↔ backend.
 *
 * Reemplaza el reparto implícito y accidental que existía antes (parte en
 * localStorage, parte en memoria efímera que se reseteaba cada sesión). Estas
 * son las prefs de UI que viajan como `moduleHints` / `sendSessionUpdate` y
 * que ahora se persisten por-usuario en `language_user_state.conversation_prefs`
 * (jsonb validado). Single source of truth de los enums: cualquier redefinición
 * local (union literal en speak) debe importar desde acá.
 *
 * NO incluye lo que tiene semántica de servidor (toggle PA, threshold) — eso
 * sigue en columnas tipadas de `language_user_state`. Tampoco incluye el
 * volumen TTS, que es device-local (vive en localStorage a propósito).
 */

import { z } from 'zod';
import { ENGLISH_INTENSITIES, SCENARIO_MODES, type EnglishIntensity } from './english.js';
import { SPANISH_VARIANTS } from './spanish-variant.js';

/** Modo de I/O (`{ input, output }` en el cliente) — voz/texto en cada sentido. */
export const CONV_MODE_IDS = ['vv', 'vt', 'tv', 'tt'] as const;
/** Gesto de captura del micrófono. */
export const MIC_MODE_IDS = ['tap', 'hold', 'auto', 'open'] as const;
/** Tamaño de las burbujas de chat. */
export const CONV_DENSITY_IDS = ['compact', 'normal', 'grande'] as const;

/**
 * Registro / estilo social del coach — qué tan formal o callejero suena.
 * Ortogonal a `intensity` (que controla DIFICULTAD): podés tener
 * "cozy + slang" o "native + formal". `neutral` reproduce el comportamiento
 * histórico (no inyecta bloque en el prompt).
 */
export const CONV_REGISTERS = ['neutral', 'casual', 'slang', 'formal', 'academic'] as const;
/**
 * «Tu inglés»: la variedad de inglés que aprende el alumno. Fuente ÚNICA desde
 * el encargo 111 (2026-10-16): manda sobre el dialecto del coach, la referencia
 * de pronunciación, los `fix` del corrector y el coach por defecto (ver
 * `english-variety.ts`). `american` = default de producto (el inglés de todos
 * los alumnos hasta que eligen otro).
 */
export const CONV_REGIONS = ['american', 'british', 'australian'] as const;
/**
 * Tono / personalidad — cómo te TRATA el coach (no qué inglés usa). `warm` =
 * persona base histórica (alentador, paciente; no inyecta bloque). Con Gemini
 * TTS el tono además fija la identidad base de la VOZ (cómo suena; tabla
 * `GEMINI_TONE_IDENTITY` del backend, `voice/tts-defaults.ts`).
 *
 * 2026-10-04 se podaron `direct`, `calm`, `enthusiastic` y `casual` tras medir
 * ~2.300 charlas simuladas: en texto no se distinguían de `warm` (0% de
 * reconocimiento a ciegas en calm/enthusiastic; casual duplicaba al ESTILO
 * casual). Los valores persistidos se reescribieron a `warm` (migración
 * `0377_prune_conv_tones.sql`); lo que quede en caches del cliente se
 * normaliza con `normalizeLegacyConvTone`.
 */
export const CONV_TONES = ['warm', 'playful', 'demanding', 'professional'] as const;

/** Tonos que existieron hasta 2026-10-04 y ya no son válidos (ver `CONV_TONES`). */
export const RETIRED_CONV_TONES = ['direct', 'calm', 'enthusiastic', 'casual'] as const;

/**
 * Migración de datos CONOCIDA para la LECTURA de prefs persistidas (jsonb sin
 * migrar, respuesta cacheada en el cliente): un tono vigente pasa tal cual, uno
 * RETIRADO se mapea a `warm`, y cualquier otra cosa devuelve `null` para que el
 * caller decida (no es un fallback silencioso: sólo los 4 valores retirados
 * tienen destino fijo). NUNCA se aplica a input nuevo: el schema de escritura
 * los rechaza.
 */
export function normalizeLegacyConvTone(value: unknown): ConvTone | null {
  if (typeof value !== 'string') return null;
  if ((CONV_TONES as readonly string[]).includes(value)) return value as ConvTone;
  if ((RETIRED_CONV_TONES as readonly string[]).includes(value)) return 'warm';
  return null;
}

/**
 * Intensidades donde estilo (`register`) y tono quedan BLOQUEADOS: en starter
 * (pre-A1) y cozy (A1) no se notan con ninguna versión del prompt y además
 * hacen que el coach pierda el andamiaje en español (medido 2026-10-04). Las
 * prefs guardadas NO se pisan: rigen de nuevo al subir a natural+. Mientras
 * tanto el efectivo es `neutral`/`warm` (ver `effectiveConvStyle`).
 */
export const CONV_STYLE_LOCKED_INTENSITIES = ['starter', 'cozy'] as const satisfies ReadonlyArray<EnglishIntensity>;

export function isConvStyleLocked(intensity: EnglishIntensity): boolean {
  return (CONV_STYLE_LOCKED_INTENSITIES as readonly string[]).includes(intensity);
}

/** Estilo y tono EFECTIVOS para una intensidad (bloqueados → `neutral`/`warm`). */
export function effectiveConvStyle(prefs: {
  intensity: EnglishIntensity;
  register: ConvRegister;
  tone: ConvTone;
}): { register: ConvRegister; tone: ConvTone } {
  if (isConvStyleLocked(prefs.intensity)) return { register: 'neutral', tone: 'warm' };
  return { register: prefs.register, tone: prefs.tone };
}

/**
 * Idioma elegido para la "Expandir explicación" de una corrección.
 *   native — idioma del learner (español).
 *   target — idioma que aprende (inglés).
 * `null` = sin elección explícita → el backend resuelve por nivel CEFR (low →
 * native, high → target). Una vez que el user togglea, queda fijado y persiste.
 */
export const EXPLAIN_LANGS = ['native', 'target'] as const;
export type ExplainLang = (typeof EXPLAIN_LANGS)[number];

/**
 * Nivel de sensibilidad de las correcciones de gramática — cuánta corrección
 * quiere ver el usuario. Filtra por la severidad mínima de cada corrección
 * (`CorrectionSeverity` del backend):
 *   - `essential` — solo errores que rompen o son claramente gramaticales
 *     (`communication-breaking` + `structural`).
 *   - `balanced`  — + `minor` (deslices chicos). Default del tramo medio.
 *   - `all`       — + `suggestion` (naturalidad / upgrades opcionales). "Modo exigente".
 * El default efectivo se deriva del CEFR (auto); ver `correctionAuto`.
 */
export const CORRECTION_LEVELS = ['essential', 'balanced', 'all'] as const;
export type CorrectionLevel = (typeof CORRECTION_LEVELS)[number];

/**
 * Etapa de andamiaje del nivel A0 (pre-A1) — cuánta muleta en español usa el
 * coach mientras el alumno arranca de cero. Es una propiedad del DIAGNÓSTICO
 * A0, no de la intensidad: se persiste en la columna `language_user_state.a0_stage`
 * con el invariante `(cefr_level='A0') ⇔ (a0_stage IS NOT NULL)`, y se retira a
 * medida que el alumno produce inglés (zero → full → medium → light → A1 sin
 * español):
 *   - `zero`   — el español es el MARCO y el inglés el contenido: el coach habla
 *                en español, mete frases cortas en inglés y lo invita a
 *                probarlas; acepta respuestas en español. Para el que arranca de
 *                CERO ("ni hola sabe"). Plan 48.
 *   - `full`   — máximo apoyo dentro del inglés: la pregunta va en inglés y el
 *                español la sostiene (consignas, traducciones, baja la ansiedad).
 *   - `medium` — apoyo moderado: mayormente inglés simple con traducciones
 *                cortas; el español sólo destraba.
 *   - `light`  — mínimo: casi todo inglés, una palabra de español sólo si se
 *                traba. Es el puente natural hacia A1.
 * Sólo existe (no-null) cuando `cefr_level='A0'`; al promover a A1+ se nulifica.
 * Default `zero` (el caso más seguro, plan 48). Ex `STARTER_SCAFFOLDINGS` (plan 31).
 */
export const A0_STAGES = ['zero', 'full', 'medium', 'light'] as const;
export type A0Stage = (typeof A0_STAGES)[number];

/**
 * Idioma con el que el STT (ElevenLabs Scribe) escucha al usuario.
 *   - `auto` — autodetect multilingüe: el alumno puede hablar en su idioma
 *     nativo (español) y se transcribe igual. Útil para el principiante.
 *   - `en`   — fuerza inglés (`language_code=en`): mejor precisión cuando
 *     realmente hablás inglés. Evita el misdetect de frases cortas con acento
 *     (Scribe en autodetect clava "hello" como portugués/italiano).
 * `null` = sin elección explícita → el backend resuelve POR NIVEL: A0 (o nivel
 * aún desconocido) → `auto`, A1+ → `en`. Al togglear queda fijado y persiste.
 * El `language_code` se fija en el handshake del WS y NO se puede mutar
 * mid-sesión, así que es un ajuste de INICIO de charla (surte efecto al
 * empezar la próxima conversación).
 */
export const STT_LANGUAGE_MODES = ['auto', 'en'] as const;
export type SttLanguageMode = (typeof STT_LANGUAGE_MODES)[number];

export type ConvRegister = (typeof CONV_REGISTERS)[number];
export type ConvRegion = (typeof CONV_REGIONS)[number];
export type ConvTone = (typeof CONV_TONES)[number];

/** Rango oficial del package de voz (ElevenLabs preserva pitch nativo en 0.7–1.2). */
export const CONVERSATION_SPEED_MIN = 0.7;
export const CONVERSATION_SPEED_MAX = 1.2;

export const conversationPrefsSchema = z.object({
  mode: z.enum(CONV_MODE_IDS),
  micMode: z.enum(MIC_MODE_IDS),
  intensity: z.enum(ENGLISH_INTENSITIES),
  /**
   * Velocidad de habla del coach (valor real del package, 0.7–1.2). Tri-state
   * como `sttLanguage`/`explainLang`: `null` = sin elección explícita → el
   * backend resuelve POR NIVEL (`recommendedSpeedForCefr`: A0/A1 más lento, A2+
   * natural) y el `/state` expone `recommendedSpeed`. Al elegir una velocidad
   * queda fijada y persiste. Se fija en el handshake del WS (`?speechRate=`):
   * con pref null el cliente NO lo manda y el server resuelve por nivel efectivo.
   */
  speed: z
    .number()
    .min(CONVERSATION_SPEED_MIN)
    .max(CONVERSATION_SPEED_MAX)
    .nullable()
    .default(null),
  phonetic: z.boolean(),
  translation: z.boolean(),
  /**
   * Permite que el coach busque en internet durante la charla (web search
   * nativo del provider). El turno que busca sale más caro que uno normal
   * (los resultados entran como input tokens). Default `false` — es **opt-in**:
   * el usuario lo activa en Ajustes cuando quiere hablar de actualidad. El
   * admin puede además deshabilitarlo globalmente vía
   * `module_config.languages.webSearchEnabled`, y sólo aplica si el modelo lo
   * soporta. Surte efecto al iniciar la sesión.
   */
  webSearch: z.boolean().default(false),
  /** Master on/off del canal de correcciones de gramática. */
  correction: z.boolean(),
  /**
   * Modo manos libres: además de los chips, el coach DICE en voz alta la
   * corrección más grave del turno (solo `communication-breaking`/`structural`,
   * máx. 1 por turno) como coda después de su respuesta. Pensado para manejar /
   * gym, donde no se puede leer. Solo surte efecto con salida de voz y con
   * `correction` ON; el admin puede apagarlo global vía
   * `module_config.languages.voiceCorrectionsEnabled`. Default `false` (opt-in).
   */
  voiceCorrections: z.boolean().default(false),
  /**
   * Sensibilidad de las correcciones (filtro por severidad mínima). Solo se
   * usa cuando `correctionAuto=false`; con auto, el efectivo se deriva del CEFR
   * (`recommendedCorrectionLevelForCefr`). Default `balanced`.
   */
  correctionLevel: z.enum(CORRECTION_LEVELS).default('balanced'),
  /**
   * Si la sensibilidad sigue la recomendación por CEFR (auto) o la elección
   * manual del usuario. Espejo de `levelAuto` para la intensidad. Default true.
   */
  correctionAuto: z.boolean().default(true),
  density: z.enum(CONV_DENSITY_IDS),
  /** Registro / estilo social del coach (formal ↔ slang). Ortogonal a la dificultad. */
  register: z.enum(CONV_REGISTERS),
  /** «Tu inglés» (fuente única de la variedad; ver `english-variety.ts`). */
  region: z.enum(CONV_REGIONS),
  /**
   * Tono / personalidad del coach (cómo te trata). Es el schema de ESCRITURA
   * (PUT): un tono retirado (`RETIRED_CONV_TONES`) se RECHAZA como cualquier
   * otro inválido. La migración `warm` de datos viejos vive sólo en la lectura
   * (`parseConversationPrefs`, `getConversationPrefs` del cliente).
   */
  tone: z.enum(CONV_TONES),
  /**
   * Idioma de la "Expandir explicación" de correcciones. `null` = sin elección
   * explícita (el backend decide por nivel); al togglear queda fijado.
   */
  explainLang: z.enum(EXPLAIN_LANGS).nullable().default(null),
  /**
   * Idioma del STT. `null` = por nivel (A0/sin-nivel → `auto`, A1+ → `en`); al
   * togglear queda fijado. Se fija en el handshake del WS → aplica al iniciar
   * la charla. Ver `STT_LANGUAGE_MODES`.
   */
  sttLanguage: z.enum(STT_LANGUAGE_MODES).nullable().default(null),
  /**
   * Limpieza del transcript del STT (mapea a `no_verbatim` de ElevenLabs
   * Scribe). `false` (default) = verbatim: se transcribe tal cual, con
   * muletillas y arranques en falso. `true` = saca "eh"/"um"/dudas/disfluencias.
   * También se fija en el handshake → aplica al iniciar la charla.
   */
  cleanTranscript: z.boolean().default(false),
  /**
   * "Español del profe": variedad del español del ANDAMIAJE (explicaciones,
   * ayudas, tips) — no del inglés que se practica (eso es `region`). Tri-state
   * como `speed`: `null` = sin elección explícita → el backend resuelve por país
   * de origen (y sin país, rioplatense histórico). Contrato completo en
   * `spanish-variant.ts`. Aplica en vivo por `session.update {spanishVariant}`.
   */
  spanishVariant: z.enum(SPANISH_VARIANTS).nullable().default(null),
  /**
   * Charla asistida: cómo practica los escenarios («solo» | «asistida»).
   * Tri-state como `speed`: `null` = nunca eligió → la pantalla previa arranca
   * por el nivel (`defaultScenarioMode`: A0–A2 asistida, B1–C2 solo). Al elegir
   * en el selector queda fijado y vale para TODOS los escenarios hasta que lo
   * cambie (decisión de Emmanuel, 13/10/2026).
   */
  scenarioMode: z.enum(SCENARIO_MODES).nullable().default(null),
  /**
   * Repaso de correcciones: ya vio la explicación del juego (la escribe el
   * coach en la primera tarjeta y al pasar a «sin mirar»). Sale UNA vez por
   * cuenta, no en cada repaso; después queda «¿Cómo se juega?» (Emmanuel, 16/10).
   */
  reviewGuideSeen: z.boolean().default(false),
});

export type ConversationPrefs = z.infer<typeof conversationPrefsSchema>;

export const CONVERSATION_PREFS_DEFAULTS: ConversationPrefs = {
  mode: 'vv',
  micMode: 'tap',
  intensity: 'natural',
  speed: null,
  phonetic: false,
  translation: false,
  webSearch: false,
  correction: true,
  voiceCorrections: false,
  correctionLevel: 'balanced',
  correctionAuto: true,
  density: 'normal',
  register: 'neutral',
  region: 'american',
  tone: 'warm',
  explainLang: null,
  sttLanguage: null,
  cleanTranscript: false,
  spanishVariant: null,
  scenarioMode: null,
  reviewGuideSeen: false,
};

/**
 * Lee prefs crudas (jsonb de la DB, body de un request) y devuelve un objeto
 * COMPLETO y válido. Mergea sobre los defaults primero, así una fila vieja con
 * `{}` (el default de la columna) o un objeto parcial / con keys futuras
 * desconocidas degrada con gracia a defaults en vez de romper. Si tras el merge
 * algún campo sigue inválido, cae a defaults enteros (no confiar en datos rotos).
 */
export function parseConversationPrefs(value: unknown): ConversationPrefs {
  const base =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const merged = { ...CONVERSATION_PREFS_DEFAULTS, ...base };
  // Tono retirado el 2026-10-04 en un dato PERSISTIDO → `warm` (migración
  // conocida, ver `normalizeLegacyConvTone`); sin esto la fila entera caería a
  // defaults. Un tono inválido de otro tipo sigue sin pasar el schema.
  const legacyTone = normalizeLegacyConvTone(merged['tone']);
  if (legacyTone !== null) merged['tone'] = legacyTone;
  const parsed = conversationPrefsSchema.safeParse(merged);
  return parsed.success ? parsed.data : CONVERSATION_PREFS_DEFAULTS;
}
